import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { pathOr } from 'ramda'
import { Row, Col } from 'antd'
import { useAbility } from '@casl/react'
import { ACTIONS, PermissionsContext, SUBJECTS } from 'context/Permissions'
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction'

import ClaimCategoriesSelect from 'components/ClaimCategoriesSelect'
import FocusRequestSelect from 'components/FocusRequestSelect'
import ClaimStatusSelect from 'components/ClaimStatusSelect'
import Claim from 'model/claims/claim'
import Attachments from 'pages/Ticket/components/RequestData/components/Attachments'
import { Label } from 'pages/Ticket/styled'
import { formatMoney, parseMoney } from 'utils/helpers'
import {
  getDateAndTimeFormat,
  DATEPICKER_FORMAT,
  DATE_FORMAT_YMD
} from 'utils/helpers/dateHelper'
import { InputNumber, Input, TextArea, Button, DatePicker } from 'ui/styled'
import useClaimAttachment from 'apollo/mutations/Claim/CLAIM_ATTACHMENT'
import useClaimUpdate from 'apollo/mutations/Claim/CLAIM_UPDATE'
import NewAttachment from './components/NewAttachment'
import { FormItem, RequestTitle, RequestText } from './styled'

const OTHER = 'other'

const BillingAdvocacyFields = ({
  claim = false,
  form,
  isSnapshot = false,
  isTicketArchived = false
}) => {
  const ability = useAbility(PermissionsContext)

  const {
    id,
    admin_notes,
    focus_request,
    medical_bills = [],
    insurance_member_id,
    created_at
  } = claim

  const { onClaimUpdate } = useClaimUpdate(false)
  const { onClaimAttachment } = useClaimAttachment(false)

  const isLimitedAccess = ability.cannot(ACTIONS.READ, SUBJECTS.ClAIMS)

  const claimCategory = pathOr(undefined, ['getClaimCategory'], claim)
  const shouldBlockUI =
    pathOr(false, ['shouldBlockUI'], claim) ||
    isTicketArchived ||
    isLimitedAccess

  const [showOther, setShowOther] = useState(focus_request === OTHER)
  const [showAdminNotes, setShowAdminNotes] = useState(!!admin_notes)
  const [attachments, setAttachments] = useState([])

  const onAddAttachment = () => {
    setAttachments([...attachments, attachments.length])
    const attachmentsField = form.getFieldValue('attachments')
    // Manual initialization of buggy ant form field
    if (!attachmentsField) form.setFieldsValue({ attachments: [] })
  }

  const onRemoveAttachment = index => {
    // console.log('onRemoveAttachment', index)
    const newArray = [...attachments]
    newArray.pop()
    let newAttachments = form.getFieldValue('attachments')
    if (!newAttachments) return false

    newAttachments = newAttachments.filter((_, i) => i !== index)

    form.setFieldsValue({ attachments: newAttachments })
    setAttachments(newArray)
  }

  const updateClaim = useCallback(
    async (key, value) => {
      if (!id) return false

      await onClaimUpdate({
        id,
        attributes: {
          [key]: value
        }
      })
    },
    [id, onClaimUpdate]
  )

  const debouncedUpdateClaim = useDebouncedFunction(updateClaim, 1000)

  const onFocusChange = value => {
    setShowOther(value === OTHER)
    updateClaim('focus_request', value)
  }

  const attachToClaim = async (file, index) => {
    // console.log('attachToClaim', file, index)
    if (!id) return false

    const { isErrorFree } = await onClaimAttachment({
      claim_id: id,
      attachments: [
        {
          file,
          title: form.getFieldValue(['attachments', index, 'title']),
          notes: form.getFieldValue(['attachments', index, 'notes'])
        }
      ]
    })

    return isErrorFree && onRemoveAttachment(index)
  }

  const updateAttachHandler = async params => {
    await onClaimAttachment({ claim_id: id, attachments: params })
  }

  const removeAttachHandler = async attachmentId => {
    await onClaimAttachment({
      claim_id: id,
      attachments: [
        {
          id: attachmentId,
          delete: true
        }
      ]
    })
  }

  return (
    <>
      {claim && (
        <>
          <RequestTitle>Claim Details</RequestTitle>
          <RequestText>
            Date submitted: {getDateAndTimeFormat(created_at)}
          </RequestText>
        </>
      )}

      {!isSnapshot && (
        <Row gutter={16}>
          <Col span={claim ? 12 : 24}>
            <Label required>Claim Type</Label>
            <FormItem
              name="claim_category"
              rules={[
                {
                  required: true,
                  message: 'Please select the claim type'
                }
              ]}
              initialValue={claimCategory}
            >
              <ClaimCategoriesSelect
                margin="0 10px 0 0"
                placeholder="Select Claim Category"
                disabled={claim}
              />
            </FormItem>
          </Col>
          {claim && (
            <Col span={12}>
              <Label required>Status</Label>
              <ClaimStatusSelect claim={claim} disabled={isLimitedAccess} />
            </Col>
          )}
        </Row>
      )}

      {claim && (
        <Row gutter={16}>
          <Col span={12}>
            <Label>Originally Billed</Label>
            <FormItem name="originally_billed">
              <InputNumber
                disabled={shouldBlockUI}
                formatter={value => formatMoney(value)}
                parser={value => parseMoney(value)}
                placeholder="0.00"
                onChange={v => debouncedUpdateClaim('originally_billed', v)}
                data-test="claimBilledNumberInput"
              />
            </FormItem>
          </Col>
          <Col span={12}>
            <Label>Amount Saved</Label>
            <FormItem name="amount_saved">
              <InputNumber
                disabled={shouldBlockUI}
                formatter={value => formatMoney(value)}
                parser={value => parseMoney(value)}
                placeholder="0.00"
                onChange={v => debouncedUpdateClaim('amount_saved', v)}
                data-test="claimAmountNumberInput"
              />
            </FormItem>
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col span={claim ? 12 : 24}>
          <Label required>Focus of Request</Label>
          <FormItem
            name="focus_request"
            rules={[
              {
                required: true,
                message: 'Please select focus'
              }
            ]}
          >
            <FocusRequestSelect
              onChange={onFocusChange}
              disabled={shouldBlockUI || isSnapshot}
              placeholder="Pick one"
              data-test="focusRequestSelect"
            />
          </FormItem>
        </Col>
        {claim && (
          <Col span={12}>
            <Label>Date of Service</Label>
            <FormItem name="date_of_service">
              <DatePicker
                placeholder="Select Date"
                format={DATEPICKER_FORMAT}
                disabled={shouldBlockUI}
                onChange={v =>
                  updateClaim(
                    'date_of_service',
                    v ? v.format(DATE_FORMAT_YMD) : v
                  )
                }
                data-test="dateOfServiceDatePicker"
              />
            </FormItem>
          </Col>
        )}
      </Row>
      {showOther && (
        <FormItem name="focus_request_other">
          <Input
            margin="15px 0 0"
            placeholder="Custom Focus"
            onChange={
              claim
                ? e =>
                    debouncedUpdateClaim('focus_request_other', e.target.value)
                : undefined
            }
            data-test="focusRequestInput"
            disabled={shouldBlockUI || isSnapshot}
          />
        </FormItem>
      )}

      <Label>Request Notes</Label>
      <FormItem name="request_notes">
        <TextArea
          rows={6}
          placeholder="Notes to add to the user"
          onChange={
            claim
              ? e => debouncedUpdateClaim('request_notes', e.target.value)
              : undefined
          }
          disabled={shouldBlockUI || isSnapshot}
          data-test="billingAdvocacyFieldsUserNotesArea"
        />
      </FormItem>

      {claim && <RequestTitle>Insurance Info</RequestTitle>}

      <Row gutter={16}>
        <Col span={12}>
          <Label>Insurance Member ID</Label>
          <FormItem
            name="insurance_member_id"
            initialValue={insurance_member_id}
          >
            <Input
              onChange={
                claim
                  ? e =>
                      debouncedUpdateClaim(
                        'insurance_member_id',
                        e.target.value
                      )
                  : undefined
              }
              placeholder="Not Available"
              data-test="insuranceMemberIdInput"
              disabled={shouldBlockUI || isSnapshot}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <Label>Insurance Carrier</Label>
          <FormItem name="insurance_carrier">
            <Input
              onChange={
                claim
                  ? e =>
                      debouncedUpdateClaim('insurance_carrier', e.target.value)
                  : undefined
              }
              placeholder="Not Available"
              data-test="insuranceCarrierInput"
              disabled={shouldBlockUI || isSnapshot}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Label>Group ID</Label>
          <FormItem name="group_id">
            <Input
              onChange={
                claim
                  ? e => debouncedUpdateClaim('group_id', e.target.value)
                  : undefined
              }
              placeholder="Populated from member data"
              data-test="groupIdInput"
              disabled={shouldBlockUI || isSnapshot}
            />
          </FormItem>
        </Col>
      </Row>

      {!showAdminNotes && (
        <Button
          onClick={() => setShowAdminNotes(true)}
          btntype="link"
          data-test="billingAdvocacyFieldsAdminNotesBtn"
          style={{ marginTop: 16 }}
        >
          + Add notes
        </Button>
      )}

      {showAdminNotes && (
        <>
          <Label>Your Notes</Label>
          <FormItem name="admin_notes">
            <TextArea
              rows={6}
              placeholder="Private notes about the claim"
              onChange={
                claim
                  ? e => debouncedUpdateClaim('admin_notes', e.target.value)
                  : undefined
              }
              disabled={shouldBlockUI}
              data-test="billingAdvocacyFieldsAdminNotesArea"
            />
          </FormItem>
        </>
      )}

      <Label>Attachments</Label>
      {claim && (
        <Attachments
          key={medical_bills.length}
          attachments={medical_bills}
          disabled={shouldBlockUI || isSnapshot}
          updateHandler={updateAttachHandler}
          removeHandler={removeAttachHandler}
        />
      )}

      {attachments.map(index => (
        <NewAttachment
          key={index}
          index={index}
          attachHandler={attachToClaim}
          onRemoveAttachment={onRemoveAttachment}
          disabled={shouldBlockUI || isSnapshot}
        />
      ))}

      <Button
        onClick={onAddAttachment}
        disabled={shouldBlockUI || isSnapshot}
        data-test="ticketAddAttachmentBtn"
      >
        Add New Attachment
      </Button>
    </>
  )
}

BillingAdvocacyFields.propTypes = {
  claim: PropTypes.oneOfType([PropTypes.shape(Claim.shape), PropTypes.bool]),
  isTicketArchived: PropTypes.bool,
  isSnapshot: PropTypes.bool,
  form: PropTypes.object
}

export default BillingAdvocacyFields
